import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { API, authHeaders, adminLogout, useAdminAuthError } from "@/lib/adminApi";
import { CURRENT_YEAR, fadeUpCss } from "@/lib/adminUtils";

const NAV_ITEMS = [
  {
    to: "/app/dashboard",
    label: "Tableau de bord",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-18v6h8V3h-8z" />
      </svg>
    ),
  },
  {
    to: "/app/membres",
    label: "Membres",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    to: "/app/paiements",
    label: "Paiements",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    to: "/app/sinistres",
    label: "Sinistres",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    to: "/app/finance",
    label: "Finance",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 2v8m0 0v2m0-2c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: "/app/parametres",
    label: "Paramètres",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const NavItems = ({ onNavigate }) => (
  <nav className="flex-1 px-3 space-y-1">
    {NAV_ITEMS.map(({ to, label, icon }) => (
      <NavLink
        key={to}
        to={to}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
            isActive
              ? "bg-white/5 text-white font-semibold border-l-2 border-emerald-500"
              : "text-gray-400 hover:text-white hover:bg-white/5"
          }`
        }
      >
        {icon}
        {label}
      </NavLink>
    ))}
  </nav>
);

export const AppLayout = () => {
  const navigate = useNavigate();
  const handleAuthError = useAdminAuthError();
  const [association, setAssociation] = useState(null);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [mobileOpen, setMobileOpen] = useState(false);

  const refreshAssociation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/associations/me`, { headers: authHeaders() });
      setAssociation(res.data);
    } catch (err) {
      handleAuthError(err);
    }
  }, [handleAuthError]);

  useEffect(() => { refreshAssociation(); }, [refreshAssociation]);

  const handleLogout = () => adminLogout(navigate);

  const sidebarInner = (
    <>
      {/* Brand */}
      <div className="px-6 pt-7 pb-6">
        <p className="text-xs font-semibold uppercase mb-2" style={{ color: "#059669", letterSpacing: "0.2em" }}>
          Hafani MS
        </p>
        <p
          className="text-xl leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, color: "#F9F6F0" }}
        >
          {association?.name ?? "…"}
        </p>
        {association?.city && (
          <p className="text-xs mt-1" style={{ color: "#6B7280" }}>{association.city}</p>
        )}
      </div>

      <NavItems onNavigate={() => setMobileOpen(false)} />

      <div className="p-4 border-t border-white/10">
        <button
          onClick={handleLogout}
          className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{fadeUpCss}</style>

      {/* Sidebar — desktop */}
      <aside
        className="w-64 flex-shrink-0 hidden lg:flex flex-col sticky top-0 h-screen"
        style={{ backgroundColor: "#111827" }}
      >
        {sidebarInner}
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <header className="sticky top-0 z-40" style={{ backgroundColor: "#111827" }}>
          <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {/* Mobile hamburger */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                    <Menu className="w-5 h-5" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0 flex flex-col border-0" style={{ backgroundColor: "#111827" }}>
                  {sidebarInner}
                </SheetContent>
              </Sheet>
              <p className="lg:hidden text-sm font-semibold" style={{ color: "#F9F6F0" }}>
                {association?.name ?? "Hafani"}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 transition-all"
                style={{ backgroundColor: "#1F2937", color: "#D1D5DB", border: "1px solid #374151" }}
              >
                {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </header>

        {/* Stripe pending banner */}
        {association?.stripe_account_id && !association?.stripe_charges_enabled && (
          <div style={{ backgroundColor: "#92400E" }}>
            <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2.5 text-sm" style={{ color: "#FDE68A" }}>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Activation Stripe en cours — vos adhérents peuvent payer par PayPal en attendant.</span>
              </div>
              <button
                onClick={() => navigate("/app/stripe-onboarding")}
                className="flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-lg transition-all hover:opacity-90"
                style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#FDE68A" }}
              >
                Reprendre →
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 px-4 sm:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Outlet context={{ association, refreshAssociation, year, setYear }} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
